import React, {useMemo} from 'react';

import {MainColumnCell, MainColumnHeader, getPaginatedRowNumber} from 'src/components/main-column';
import {useDataTableColumnsWidthOnResize} from 'src/hooks/useDataTableColumnsWidthOnResize';
import {ActionTimestampCell} from 'src/modules/manage-entitlements/edit-role/change-log/cells';
import {EmailDeliveryLog} from 'src/openapi/admin/models/EmailDeliveryLog';

import {
    Cell,
    type CellOptions,
    Column, DataTable,
    HeaderCellOptions,
    type RowsParams,
    TableHeader,
} from '@pb/kit-component-data-table-v2';
import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';


import {COLUMNS, COLUMNS_DATA, HEADERS} from '../constants';

import {DeliveryStatusCell} from './cells/delivery-status-cell';
import {EmailInitiatorCell} from './cells/email-initiator-cell';
import {EmailTypeCell} from './cells/email-type-cell';
import {UserEmailDeliveryStatusLogPagination} from './filters/email-delivery-status-log-pagination';


import type {DictionaryItem} from 'src/openapi/admin/models/DictionaryItem';
import type {Pagination} from 'src/openapi/admin/models/Pagination';

type Props = {
    rows: EmailDeliveryLog[],
    totalCount: number,
    pagination: Pagination,
    trackedEmailType: DictionaryItem[],
};

export const EmailDeliveryHistoryTable = ({rows, totalCount, pagination, trackedEmailType}: Props) => {
    const {getColumnWidthByColumnId, onStopResize} = useDataTableColumnsWidthOnResize();

    const renderedRows = useMemo(() => (rows
        .map(log => ({
            id: log.id,
            columnValues: {
                email: log.email,
                status: log.status,
                initiatorUser: log.initiatorUser,
                statusChangeDate: log.statusChangeDate,
            },
        }))
    ), [rows]);

    const maxRowNumber = getPaginatedRowNumber({...pagination, index: renderedRows?.length});

    return (
        <DataTable
            id="user-email-delivery-log-table"
            data={{
                rows: renderedRows, columns: COLUMNS_DATA.map((column: Column) => ({
                    ...column,
                    width: getColumnWidthByColumnId(column.columnId),
                })),
            }}
            getHeaderTopOffset={() => 235}
            onStopResize={onStopResize}
            headers={({column}: {column: Column}, cellOptions: HeaderCellOptions) => {
                switch (column.columnId) {
                    case COLUMNS.EMAIL_TYPE:
                        return (
                            <Cell
                                {...cellOptions}
                                key={`${column.columnId}`}
                                column={column}
                                resizable={true}>
                                <MainColumnHeader
                                    selection={<div/>}
                                    maxRowNumber={maxRowNumber}>
                                    <strong>{HEADERS[column.columnId]}</strong>
                                </MainColumnHeader>
                            </Cell>
                        );
                    default:
                        return (
                            <Cell
                                {...cellOptions}
                                key={`${column.columnId}`}
                                column={column}
                                resizable={true}>
                                <TableHeader indent="none" className="search-header">
                                    <strong>{HEADERS[column.columnId]}</strong>
                                </TableHeader>
                            </Cell>
                        );
                }
            }}
            renderRows={(rowsParams: RowsParams, cellOptions: CellOptions) => {
                const {rowIndex, column: {columnId}} = rowsParams;
                const value = rowsParams.row.columnValues[columnId];
                const column = rowsParams.column;
                const rowId = +rowsParams.row.id;
                const cellKey = `${rowId}-${columnId}`;
                const rowNumber = getPaginatedRowNumber({
                    ...pagination,
                    index: rowIndex + 1,
                });
                switch (columnId) {
                    case COLUMNS.EMAIL_TYPE:
                        return (
                            <MainColumnCell
                                rowId={rowId}
                                key={cellKey}
                                selection={<div/>}
                                rowNumber={rowNumber}
                                maxRowNumber={maxRowNumber}
                                cellOptions={{...cellOptions, column}}>
                                <EmailTypeCell
                                    emailTypeCode={value as string}
                                    trackedEmailTypes={trackedEmailType}
                                />
                            </MainColumnCell>
                        );
                    case COLUMNS.STATUS:
                        return (
                            <Cell
                                {...cellOptions}
                                key={cellKey}
                                column={column}
                                className="search-result-cell">
                                <DeliveryStatusCell
                                    statusCode={
                                        value as React.ComponentProps<typeof DeliveryStatusCell>['statusCode']
                                    }
                                />
                            </Cell>
                        );
                    case COLUMNS.TIMESTAMP:
                        return (
                            <Cell
                                {...cellOptions}
                                key={cellKey}
                                column={column}
                                className="search-result-cell">
                                <ActionTimestampCell timestamp={value as string}/>
                            </Cell>
                        );
                    case COLUMNS.INITIATOR:
                        return (
                            <Cell
                                {...cellOptions}
                                key={cellKey}
                                column={column}
                                className="search-result-cell">
                                <EmailInitiatorCell
                                    user={value as React.ComponentProps<typeof EmailInitiatorCell>['user']}
                                />
                            </Cell>
                        );
                    default:
                        return (
                            <Cell
                                {...cellOptions}
                                key={cellKey}
                                column={column}
                                className="search-result-cell">
                                <EllipsisTextHint title={String(value)}>
                                    {String(value)}
                                </EllipsisTextHint>
                            </Cell>
                        );
                }
            }}
            paginationStripe={<UserEmailDeliveryStatusLogPagination totalCount={totalCount}/>}
        />
    );
};
